import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isFilledString from "~/helpers/isFilledString";

export const LABOR_RATES_BASE_PATH = "/produce/labor/rates";

export const LABOR_RATES_DOCK_PANEL_ID = "labor-rates";

export const LABOR_RATE_HISTORY_TARGET_KIND = "laborRateHistory";

export const LABOR_RATES_SOURCE_KIND = "laborRates";

export interface LaborRateHistoryScope {
	effectiveOn: null | string;
	laborTypeId: null | string;
	readOnly?: boolean;
	siteLocationId: null | string;
}

export interface LaborRatesSourceCheckpoint {
	laborTypeId: string;
	laborTypeName: string;
	siteLocationId: string;
}

const optionalId = (value: unknown): null | string => (isFilledString(value) ? value : null);

export const buildLaborRateHistoryTarget = ({
	effectiveOn,
	laborTypeId,
	readOnly,
	siteLocationId
}: LaborRateHistoryScope): ContinuationTarget => ({
	targetId: JSON.stringify({ effectiveOn, laborTypeId, siteLocationId, ...(readOnly ? { readOnly: true } : {}) }),
	targetKind: LABOR_RATE_HISTORY_TARGET_KIND
});

const parseTargetId = (targetId: string): Record<string, unknown> | null => {
	try {
		const parsed: unknown = JSON.parse(targetId);
		return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
	} catch {
		return null;
	}
};

export const readLaborRateHistoryScope = (target: ContinuationTarget | undefined): LaborRateHistoryScope | null => {
	if (target?.targetKind !== LABOR_RATE_HISTORY_TARGET_KIND) return null;
	const parsed = parseTargetId(target.targetId);
	const siteLocationId = optionalId(parsed?.siteLocationId);
	const laborTypeId = optionalId(parsed?.laborTypeId);
	if (!siteLocationId && !laborTypeId) return null;
	return {
		effectiveOn: optionalId(parsed?.effectiveOn),
		laborTypeId,
		siteLocationId,
		...(parsed?.readOnly === true ? { readOnly: true } : {})
	};
};

export const laborRateHistoryReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => (readLaborRateHistoryScope(target) ? LABOR_RATES_BASE_PATH : ""),
	matchesRoute: () => null
};

export const isLaborRatesSourceCheckpoint = (value: unknown): value is LaborRatesSourceCheckpoint =>
	typeof value === "object" &&
	value !== null &&
	isFilledString((value as Record<string, unknown>).laborTypeId) &&
	typeof (value as Record<string, unknown>).laborTypeName === "string" &&
	isFilledString((value as Record<string, unknown>).siteLocationId);

const buildLaborRatesEditorKey = (checkpoint: LaborRatesSourceCheckpoint): string =>
	`${LABOR_RATES_DOCK_PANEL_ID}:${checkpoint.siteLocationId}:${checkpoint.laborTypeId}`;

export default buildLaborRatesEditorKey;
